/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * certificateTemplates
 * --------------------
 * Pure helpers for the Certificate Templates screen: the form, its checks,
 * the batch it saves, and turning an uploaded file into the link stored in
 * `template_url`.
 *
 * Every read and write goes through the existing CRUD endpoints:
 *   POST /arcee/application/table/fetch/          the list
 *   POST /arcee/application/table/save/records/   add, edit
 * and the file-storage server's own /upload, as the File Manager does.
 */

export const CERTIFICATE_TEMPLATE_TABLE = 'certificate_template';

export const LIMITS = { name: 64, description: 256 };

/**
 * The placeholders a template's HTML may use. organization_* are given by the
 * organizer for each activity; the rest are filled from the certificate, its
 * pass, event and activity when the participant downloads it.
 */
export const TEMPLATE_PLACEHOLDERS = [
  { key: 'organization_name', from: 'The organization name the organizer gives for the activity' },
  { key: 'organization_logo_url', from: 'The organization logo the organizer gives (use it as <img src="{{ organization_logo_url }}">)' },
  { key: 'name', from: "The participant's full name" },
  { key: 'first_name', from: "The participant's first name" },
  { key: 'last_name', from: "The participant's last name" },
  { key: 'event', from: 'The event title' },
  { key: 'activity', from: 'The activity name' },
  { key: 'event_date', from: 'The event date, e.g. 18 October 2026' },
  { key: 'event_date_short', from: 'The event date, e.g. 18 Oct 2026' },
  { key: 'organizer', from: "The event organizer's name" },
  { key: 'certificate_no', from: 'The certificate number' },
];

export function initialTemplateForm() {
  return { id: null, name: '', description: '', template_url: '', is_active: true };
}

/** A fetched row → the form. */
export function hydrateTemplateForm(row) {
  if (!row) return null;
  return {
    id: row.id ?? null,
    name: row.name || '',
    description: row.description || '',
    template_url: row.template_url || '',
    is_active: row.is_active !== false,
  };
}

const isLink = (value) => /^https?:\/\/\S+$/i.test(value);

/** { field: message } for every problem; empty when the form can be saved. */
export function validateTemplateForm(form, rows = []) {
  const errors = {};
  const name = (form.name || '').trim();
  if (!name) errors.name = 'Enter a template name.';
  else if (name.length > LIMITS.name) errors.name = `At most ${LIMITS.name} characters.`;
  else if (rows.some((row) => row.id !== form.id
    && String(row.name || '').trim().toLowerCase() === name.toLowerCase())) {
    errors.name = 'Another template already has this name.';
  }

  if ((form.description || '').length > LIMITS.description) {
    errors.description = `At most ${LIMITS.description} characters.`;
  }

  const templateUrl = (form.template_url || '').trim();
  if (!templateUrl) errors.template_url = 'Upload the template HTML file.';
  else if (!isLink(templateUrl)) errors.template_url = 'Upload the template HTML file again.';

  return errors;
}

/** One add (no id) or edit (with id) of a template, in one batch. */
export function buildTemplateBatch(form, { organizationId, applicationId }) {
  const data = {
    name: form.name.trim(),
    description: (form.description || '').trim() || null,
    template_url: form.template_url.trim(),
    is_active: Boolean(form.is_active),
  };
  if (form.id !== null && form.id !== undefined && form.id !== '') data.id = form.id;
  return {
    organization_id: organizationId,
    application_id: applicationId,
    operations: [{ table: CERTIFICATE_TEMPLATE_TABLE, data }],
  };
}

/** Rows by name, active templates first. */
export function sortTemplates(rows) {
  return [...(rows || [])].sort((left, right) => {
    const byActive = Number(right.is_active !== false) - Number(left.is_active !== false);
    return byActive || String(left.name || '').localeCompare(String(right.name || ''));
  });
}

/**
 * The file-storage server refuses .html uploads (an HTML file served from it
 * could run scripts in a visitor's browser). A template is therefore stored as
 * plain text: same content, a .txt name and text/plain type. The certificate
 * pages read it as text and render it themselves, so it never runs as a page
 * on the storage server.
 */
export function asTextUpload(file) {
  const base = (file.name || 'template').replace(/\.[^.]+$/, '') || 'template';
  return new File([file], `${base}.txt`, { type: 'text/plain' });
}

export const isHtmlFile = (file) => /\.html?$/i.test(file?.name || '');

/** The storage server's public_url ("/files/…") as a full link. */
export function publicFileLink(fileServerUrl, publicUrl) {
  if (!publicUrl) return '';
  if (isLink(publicUrl)) return publicUrl;
  return `${String(fileServerUrl || '').replace(/\/$/, '')}${publicUrl}`;
}

/** The organizer's choice of template per event activity (certificate_template_id). */
export const CHOICE_TABLE = 'event_activity_certificate_template';

const idOf = (value) => {
  if (value && typeof value === 'object') return value.id ?? value.data?.id;
  return value;
};

/** { templateId: how many event activities use it }. */
export function usageByTemplate(choiceRows) {
  const out = {};
  (choiceRows || []).forEach((row) => {
    const id = idOf(row.certificate_template_id);
    if (id === null || id === undefined || id === '') return;
    out[String(id)] = (out[String(id)] || 0) + 1;
  });
  return out;
}

/**
 * The file-storage id in a template link — the last part of
 * <file server>/files/<org>/<app>/<id> (or /files/<id>); '' when it is not one.
 */
export function fileIdFromUrl(url) {
  const match = /\/files\/(?:\d+\/[^/?#]+\/)?([^/?#]+)\/?(?:[?#].*)?$/.exec(String(url || ''));
  return match ? match[1] : '';
}

/**
 * Deletes a template's file from the file-storage server (DELETE /files/<id>; the server checks
 * it belongs to this organization). True when it is gone (204, or 404: already gone).
 */
export async function deleteTemplateFile(url, { organizationId, applicationId, fileServerUrl, apiKey, fetchImpl = fetch }) {
  const id = fileIdFromUrl(url);
  if (!id) return false;
  const headers = { 'X-API-KEY': apiKey, 'X-ORG-ID': String(organizationId) };
  if (applicationId) headers['X-APP-ID'] = String(applicationId);
  const resp = await fetchImpl(`${String(fileServerUrl || '').replace(/\/$/, '')}/files/${encodeURIComponent(id)}`, {
    method: 'DELETE', headers,
  });
  return resp.status === 204 || resp.status === 404 || resp.ok;
}

/** Loaders stay up at least this long, so a quick upload or delete is still seen. */
export const MIN_LOADER_MS = 3000;

/** Resolves once at least MIN_LOADER_MS have passed since `startedAt` (a Date.now() value). */
export function afterMinimumLoader(startedAt, now = Date.now(), wait = (ms) => new Promise((r) => setTimeout(r, ms))) {
  const left = MIN_LOADER_MS - (now - startedAt);
  return left > 0 ? wait(left) : Promise.resolve();
}
